import React, { useMemo, useContext } from 'react';
import { ScreenClassContext } from 'react-grid-system';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { StyledComponent } from 'styled-components';

import { Theme } from 'ia-react-core/~/interfaces/Theme';
import { ResponsiveProps } from 'ia-react-core/~/interfaces/ResponsiveProps';
import { Breakpoint } from 'ia-react-core/~/interfaces/Breakpoint';
import keys from 'ia-react-core/~/utils/accessors/keys';
import BREAKPOINT_LIST from '../../../constants/BREAKPOINT_LIST';
import { responsiveProps, behaviors, responsiveBehaviors } from './withSpacing.helpers';

export type SpacingType = 'm' | 'p';
export type SpacingSides = 'v' | 'h' | 't' | 'r' | 'b' | 'l';
type LastPart<S extends string> = S extends `${string}-${infer K extends Breakpoint}` ? K : never;

export type WithSpacingProps = {
  [P in SpacingType]?: number;
} & {
  [P in `${SpacingType}-${SpacingSides}`]?: number;
} & ResponsiveProps<{
  [P in SpacingType]?: number;
}> & ResponsiveProps<{
  [P in `${SpacingType}-${SpacingSides}`]?: number;
}>;

/**
 * @since 0.15.0
 */
const withSpacing = (WrappedComponent: StyledComponent<'div', Theme, WithSpacingProps>) => {
  const WithSpacing = (props: WithSpacingProps) => {
    const propsList = keys(props);
    const upperBreakpoint = useContext(ScreenClassContext);

    const hasResponsiveStyles = useMemo(() => (
      propsList.some((propName) => responsiveProps.includes(propName))
    ), [propsList]);

    if (hasResponsiveStyles) {
      const styles = getStyles(upperBreakpoint);
      return <WrappedComponent {...props} style={styles} />;
    }

    const mainStyles = getStyles();
    return <WrappedComponent {...props} style={mainStyles} />;

    function getStyles(currentBreakpoint?: Breakpoint) {
      const styles = {};

      propsList.forEach((propName) => {
        const { [propName]: value } = props;

        if (typeof value !== 'undefined') {
          if (behaviors[propName]) {
            behaviors[propName](value, styles);
          } else if (currentBreakpoint && responsiveBehaviors[propName]) {
            const breakpoint = propName.split('-').pop() as LastPart<typeof propName>;
            const index = BREAKPOINT_LIST.indexOf(breakpoint);
            if (BREAKPOINT_LIST.indexOf(currentBreakpoint) >= index) {
              responsiveBehaviors[propName](value, styles);
            }
          }
        }
      });

      return styles;
    }
  };

  return hoistNonReactStatics(WithSpacing, WrappedComponent);
};

/** @component */
export default withSpacing;
